/**
 * Seção "Regras": o que a unidade exige na autorização, o que a retirada
 * faz com a situação e as réguas (limiares) do Painel de Assistências.
 * Só entram aqui campos que JÁ têm efeito no sistema — configuração sem
 * leitor seria botão que não faz nada.
 */
import React, { useEffect, useState } from "react";

import AsyncButton from "../../../../components/AsyncButton";
import { useAuth } from "../../../../context/AuthContext";
import {
  TIPOS_SOLICITACAO,
  obterConfiguracaoSolicitacoes,
  salvarConfiguracaoSolicitacoes,
} from "../../../../services/solicitacoes";
import { SectionTitle } from "../../../../styles/shared";
import { Carregando } from "../../_shared/Estados";
import {
  Checkbox, ErrorMsg, Field, InlineError, Input, Label, PecaCard, PecaLinha, RowEnd,
} from "../styles";
import { LIMIARES, parseFaixas, rotulosDasFaixas, validarLimiares } from "./regras";

const linhaCheck = { display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13 };

export default function RegrasSecao({ podeEditar }) {
  const { request } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [faixasTexto, setFaixasTexto] = useState("");
  const [erro, setErro] = useState("");
  const [erroEnvio, setErroEnvio] = useState("");
  const [sujo, setSujo] = useState(false);

  const aplicar = (c) => {
    setCfg(c);
    setFaixasTexto((c.faixas_idade_du || []).join(", "));
    setSujo(false);
  };

  useEffect(() => {
    obterConfiguracaoSolicitacoes(request).then(aplicar)
      .catch((e) => setErro(e?.message || "Erro ao carregar a configuração."));
  }, [request]);

  const mudar = (campo, valor) => {
    setErroEnvio("");
    setSujo(true);
    setCfg((c) => ({ ...c, [campo]: valor }));
  };

  const alternarTipo = (tipo) => {
    const atual = cfg.tipos_exigem_classificacao || [];
    mudar("tipos_exigem_classificacao",
      atual.includes(tipo) ? atual.filter((t) => t !== tipo) : [...atual, tipo]);
  };

  const salvar = async () => {
    const { faixas, erro: erroFaixas } = parseFaixas(faixasTexto);
    if (erroFaixas) { setErroEnvio(erroFaixas); throw new Error(erroFaixas); }
    const { valores: limiares, erro: erroLimiar } = validarLimiares(cfg);
    if (erroLimiar) { setErroEnvio(erroLimiar); throw new Error(erroLimiar); }
    try {
      aplicar(await salvarConfiguracaoSolicitacoes(request, {
        exige_classificacao_na_autorizacao: cfg.exige_classificacao_na_autorizacao,
        tipos_exigem_classificacao: cfg.tipos_exigem_classificacao,
        atender_automaticamente_na_retirada: cfg.atender_automaticamente_na_retirada,
        ...limiares,
        faixas_idade_du: faixas,
      }));
    } catch (e) {
      setErroEnvio(e?.message || "Não foi possível salvar.");
      throw e;
    }
  };

  if (erro) return <ErrorMsg>{erro}</ErrorMsg>;
  if (!cfg) return <Carregando />;

  const previa = parseFaixas(faixasTexto);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14, maxWidth: 760 }}>
      <PecaCard>
        <SectionTitle>Classificação na autorização</SectionTitle>
        <label style={linhaCheck}>
          <Checkbox checked={!!cfg.exige_classificacao_na_autorizacao} disabled={!podeEditar}
                    onChange={(e) => mudar("exige_classificacao_na_autorizacao", e.target.checked)} />
          Exigir não conformidade e processo de origem para autorizar
        </label>
        <PecaLinha>
          Ligado (padrão), a solicitação só é autorizada com não conformidade e processo em cada
          item — é o que alimenta a aba Qualidade do painel. Desligado, classificar vira opcional.
        </PecaLinha>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", opacity: cfg.exige_classificacao_na_autorizacao ? 1 : 0.55 }}>
          {TIPOS_SOLICITACAO.map((t) => (
            <label key={t.value} style={linhaCheck} title={t.label}>
              <Checkbox checked={(cfg.tipos_exigem_classificacao || []).includes(t.value)}
                        disabled={!podeEditar || !cfg.exige_classificacao_na_autorizacao}
                        onChange={() => alternarTipo(t.value)} />
              {t.value}
            </label>
          ))}
        </div>
      </PecaCard>

      <PecaCard>
        <SectionTitle>Retirada na expedição</SectionTitle>
        <label style={linhaCheck}>
          <Checkbox checked={!!cfg.atender_automaticamente_na_retirada} disabled={!podeEditar}
                    onChange={(e) => mudar("atender_automaticamente_na_retirada", e.target.checked)} />
          Marcar como Atendida automaticamente quando a retirada for completa
        </label>
        <PecaLinha>
          Só se marca "Atendida" depois da retirada assinada de todos os itens. Desligado, a retirada
          completa registra "Expedida" e alguém confirma "Atendida" à mão (útil enquanto "atendida"
          significar peça instalada na obra).
        </PecaLinha>
      </PecaCard>

      <PecaCard>
        <SectionTitle>Réguas do Painel de Assistências</SectionTitle>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
          {LIMIARES.map(({ campo, rotulo, minimo, dica }) => (
            <Field key={campo}>
              <Label>{rotulo}</Label>
              <Input type="number" min={minimo} value={cfg[campo] ?? ""} disabled={!podeEditar}
                     onChange={(e) => mudar(campo, e.target.value)} />
              <PecaLinha>{dica}</PecaLinha>
            </Field>
          ))}
          <Field>
            <Label>Faixas de idade das abertas (dias úteis)</Label>
            <Input value={faixasTexto} disabled={!podeEditar} placeholder="2, 5, 10, 20"
                   onChange={(e) => { setFaixasTexto(e.target.value); setSujo(true); setErroEnvio(""); }} />
            <PecaLinha>
              {previa.erro ? previa.erro : `O painel mostra: ${rotulosDasFaixas(previa.faixas).join(" · ")}`}
            </PecaLinha>
          </Field>
        </div>
      </PecaCard>

      {cfg.atualizado_por_nome && (
        <PecaLinha>Última alteração por {cfg.atualizado_por_nome}.</PecaLinha>
      )}
      {erroEnvio && <InlineError style={{ fontSize: 12.5 }}>{erroEnvio}</InlineError>}
      {podeEditar && (
        <RowEnd>
          <AsyncButton variant="primary" silentError successMessage="Configuração salva."
                       onClick={salvar} disabled={!sujo}>
            Salvar regras
          </AsyncButton>
        </RowEnd>
      )}
    </div>
  );
}
